/**
 * 一元配置 ANOVA 事後のペアワイズ比較（Tukey HSD / Tukey–Kramer）。
 * ダッシュボードのグラフ描画（ブラケット＋***）用に leftIndex, rightIndex, pValue を返す。
 *
 * 調整済み p は R の `TukeyHSD.aov` と同じ定式（不等 n を含む）:
 * `est = |μ̂_i−μ̂_j| / sqrt(MSE/2·(1/n_i+1/n_j))`、`p adj = ptukey(abs(est), k, df, lower.tail=FALSE)`。
 * プール t との関係は `est = √2·|t|`（|t| = |差| / sqrt(MSE·(1/n_i+1/n_j))）。
 */

import type { ANOVAResult } from './anova';
import { ptukeyLowerTail } from './studentizedRange';
import { twoTailedPValue } from './tTest';

/**
 * プールされた絶対 t（|mean_i−mean_j| / sqrt(MS_w(1/n_i+1/n_j))）に対する
 * Tukey–Kramer（R `TukeyHSD.aov`）の調整済み p 値。
 * - k = 2: 多重比較がないため両側プール t と一致。
 * - k ≥ 3: `ptukey(√2·|t|, 1, k, df)` の上側確率（`studentizedRange.ts` の R 同型 ptukey）。
 */
export function tukeyPairwiseAdjustedP(absPooledT: number, k: number, df: number): number {
  if (!Number.isFinite(absPooledT) || absPooledT < 0) {
    return NaN;
  }
  if (k < 2 || !Number.isFinite(df) || df < 2) {
    return NaN;
  }
  if (k === 2) {
    return twoTailedPValue(absPooledT, df);
  }
  const qR = Math.SQRT2 * absPooledT;
  const cdf = ptukeyLowerTail(qR, 1, k, df);
  if (!Number.isFinite(cdf)) {
    return NaN;
  }
  return Math.max(0, Math.min(1, 1 - cdf));
}

/** 既に R の studentized range 尺度 Q（≥0）にある統計量から調整済み P を返す（LMM 事後の q 用） */
export function tukeyFromStudentizedRangeQ(qR: number, k: number, df: number): number {
  if (!Number.isFinite(qR) || qR < 0 || k < 2 || !Number.isFinite(df) || df < 2) {
    return NaN;
  }
  if (k === 2) {
    return twoTailedPValue(qR / Math.SQRT2, df);
  }
  const cdf = ptukeyLowerTail(qR, 1, k, df);
  if (!Number.isFinite(cdf)) {
    return NaN;
  }
  return Math.max(0, Math.min(1, 1 - cdf));
}

/**
 * @deprecated `tukeyPairwiseAdjustedP(q, k, df)` を使用してください。
 */
export function approximateTukeyPValue(qValue: number, df: number, k: number): number {
  return tukeyPairwiseAdjustedP(qValue, k, df);
}

/**
 * 一元配置 ANOVA 結果に対して Tukey HSD の全ペアを計算し、
 * 有意（p < 0.05）なペアのみ { leftIndex, rightIndex, pValue } の配列で返す
 * インデックスは result.groups の並び順（棒グラフのカテゴリ順）に一致
 */
export function computeTukeySignificancePairs(
  result: ANOVAResult
): Array<{ leftIndex: number; rightIndex: number; pValue: number }> {
  const groups = result.groups;
  const k = groups.length;
  if (k < 2) return [];

  const avgN = groups.reduce((acc, g) => acc + g.n, 0) / k;
  const mswOverN = avgN > 0 ? result.msWithin / avgN : 0;
  const qDenominator = mswOverN > 0 ? Math.sqrt(mswOverN) : 0;
  if (qDenominator <= 0) return [];

  const pairs: Array<{ leftIndex: number; rightIndex: number; pValue: number }> = [];

  for (let i = 0; i < k; i += 1) {
    for (let j = i + 1; j < k; j += 1) {
      const diff = Math.abs(groups[j].mean - groups[i].mean);
      const se =
        groups[i].n > 0 && groups[j].n > 0
          ? Math.sqrt(result.msWithin * (1 / groups[i].n + 1 / groups[j].n))
          : 0;
      const qValue = se > 0 ? diff / se : 0;
      const pValue = tukeyPairwiseAdjustedP(qValue, k, result.dfWithin);
      if (pValue < 0.05) {
        pairs.push({ leftIndex: i, rightIndex: j, pValue });
      }
    }
  }

  return pairs;
}

/**
 * 一元配置 ANOVA 結果に対して Tukey HSD の全ペアを計算し、
 * 有意・非有意を問わずすべて { leftIndex, rightIndex, pValue } の配列で返す（群間有意差一覧表示用）
 */
export function computeTukeyAllPairs(
  result: ANOVAResult
): Array<{ leftIndex: number; rightIndex: number; pValue: number; significant: boolean }> {
  const groups = result.groups;
  const k = groups.length;
  if (k < 2) return [];

  const avgN = groups.reduce((acc, g) => acc + g.n, 0) / k;
  const mswOverN = avgN > 0 ? result.msWithin / avgN : 0;
  const qDenominator = mswOverN > 0 ? Math.sqrt(mswOverN) : 0;
  if (qDenominator <= 0) return [];

  const pairs: Array<{ leftIndex: number; rightIndex: number; pValue: number; significant: boolean }> = [];

  for (let i = 0; i < k; i += 1) {
    for (let j = i + 1; j < k; j += 1) {
      const diff = Math.abs(groups[j].mean - groups[i].mean);
      const se =
        groups[i].n > 0 && groups[j].n > 0
          ? Math.sqrt(result.msWithin * (1 / groups[i].n + 1 / groups[j].n))
          : 0;
      const qValue = se > 0 ? diff / se : 0;
      const pValue = tukeyPairwiseAdjustedP(qValue, k, result.dfWithin);
      pairs.push({ leftIndex: i, rightIndex: j, pValue, significant: pValue < 0.05 });
    }
  }

  return pairs;
}
